import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Badge, Modal, Form, Row, Col, Alert, Dropdown } from "react-bootstrap";
import axios from "axios";
import config from "../../config";
import { toast } from "react-toastify";
import { FaDownload, FaEye, FaTruck, FaWeightHanging, FaEnvelope, FaEdit, FaTrash, FaFileInvoice, FaWrench } from "react-icons/fa";
import ViewEditDispatchModal from "./ViewEditDispatchModal";

export default function DispatchList({ salesOrderId, onRefresh, canEditDispatch, refreshKey = 0 }) {
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [vehicleWeightAfterLoad, setVehicleWeightAfterLoad] = useState("");
  const [weightSlipAfter, setWeightSlipAfter] = useState(null);
  const [weightValidationError, setWeightValidationError] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    to: "",
    subject: "",
    message: ""
  });
  const [
    // invoiceFile, 
    setInvoiceFile
  ] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [invoiceUploadFile, setInvoiceUploadFile] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null); // Track which PDF is downloading
  const [downloadingInvoice, setDownloadingInvoice] = useState(null); // Track which invoice is downloading
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewEditModal, setShowViewEditModal] = useState(false);
  const [editData, setEditData] = useState({
    vehicleNumber: "",
    driverName: "",
    driverContact: "",
    transporter: "",
    dispatchDate: "",
    expectedDeliveryDate: "",
    remarks: ""
  });
  const [updatingDispatch, setUpdatingDispatch] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);

  const fetchDispatches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/dispatches`, {
        params: { salesOrderId }
      });
      setDispatches(res.data);
    } catch (err) {
      toast.error("Error fetching dispatches");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [salesOrderId]);

  useEffect(() => {
    if (salesOrderId) {
      fetchDispatches();
    }
  }, [salesOrderId, refreshKey, fetchDispatches]);

  // Fetch company settings on component mount
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const res = await axios.get(`${config.API_BASE_URL}/company-settings`);
        setCompanySettings(res.data);
      } catch (err) {
        console.error("Error fetching company settings:", err);
      }
    };

    fetchCompanySettings();
  }, []);

  const handleViewDetails = async (dispatchId) => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/dispatches/${dispatchId}`);
      setSelectedDispatch(res.data);
      setShowDetailsModal(true);
    } catch (err) {
      toast.error("Error fetching dispatch details");
      console.error(err);
    }
  };

  const handleDownloadPDF = async (dispatchId, dispatchNumber) => {
    setDownloadingPdf(dispatchId); // Set loading state
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/dispatches/${dispatchId}/download-pdf`,
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `DN_${dispatchNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Dispatch note PDF downloaded");
    } catch (err) {
      toast.error("Error downloading PDF");
      console.error(err);
    } finally {
      setDownloadingPdf(null); // Clear loading state
    }
  };

  const handleOpenStatusModal = (dispatch) => {
    setSelectedDispatch(dispatch);
    setNewStatus(dispatch.status);
    setShowStatusModal(true);
  };

  const handleUpdateStatus = async () => {
    try {
      await axios.patch(
        `${config.API_BASE_URL}/dispatches/${selectedDispatch._id}/status`,
        { status: newStatus }
      );

      toast.success("Dispatch status updated");
      setShowStatusModal(false);
      fetchDispatches();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error("Error updating status");
      console.error(err);
    }
  };

  // const handleDeleteDispatch = async (dispatchId, dispatchNumber) => {
  //   if (!window.confirm(`Are you sure you want to delete dispatch note ${dispatchNumber}?`)) {
  //     return;
  //   }

  //   try {
  //     await axios.delete(`${config.API_BASE_URL}/dispatches/${dispatchId}`);
  //     toast.success("Dispatch note deleted");
  //     fetchDispatches();
  //     if (onRefresh) onRefresh();
  //   } catch (err) {
  //     toast.error(err.response?.data?.message || "Error deleting dispatch note");
  //     console.error(err);
  //   }
  // };

  const handleOpenWeightModal = (dispatch) => {
    setSelectedDispatch(dispatch);
    setVehicleWeightAfterLoad("");
    setWeightSlipAfter(null);
    setWeightValidationError("");
    setShowWeightModal(true);
  };

  const handleUpdateWeight = async () => {
    // Calculate minimum weight validation
    const vehicleWeightBefore = parseFloat(selectedDispatch.vehicleWeightBeforeLoad) || 0;
    const totalDispatchedQuantity = selectedDispatch.items?.reduce((sum, item) => {
      return sum + (parseFloat(item.quantityDispatched) || 0);
    }, 0) || 0;
    const minimumWeight = vehicleWeightBefore + totalDispatchedQuantity - 100;

    const enteredWeight = parseFloat(vehicleWeightAfterLoad);

    if (enteredWeight < minimumWeight) {
      setWeightValidationError("entered weight are wrong check again");
      return;
    }

    try {
      const formData = new FormData();
      formData.append('vehicleWeightAfterLoad', vehicleWeightAfterLoad);

      if (weightSlipAfter) {
        formData.append('weightSlipAfter', weightSlipAfter);
      }

      await axios.patch(
        `${config.API_BASE_URL}/dispatches/${selectedDispatch._id}/weight-after`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success("After load weight updated successfully");
      setShowWeightModal(false);
      fetchDispatches();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error("Error updating after load weight");
      console.error(err);
    }
  };

  const handleViewWeightSlip = (filePath) => {
    if (filePath) {
      // Assuming files are served from the uploads directory
      const fullUrl = `${filePath}`;
      // const fullUrl = `${config.API_BASE_URL}/${filePath}`;
      window.open(fullUrl, '_blank');
    }
  };

  const handleDownloadInvoice = async (dispatchId, dispatchNumber) => {
    setDownloadingInvoice(dispatchId); // Set loading state
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/dispatches/${dispatchId}/download-invoice`,
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Invoice_${dispatchNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Invoice downloaded");
    } catch (err) {
      toast.error("Error downloading invoice");
      console.error(err);
    } finally {
      setDownloadingInvoice(null); // Clear loading state
    }
  };

  const handleOpenEmailModal = async (dispatch) => {
    setSelectedDispatch(dispatch);

    // Pre-fill email data
    const buyerEmail = dispatch.salesOrder?.buyer?.business_email || "";

    setEmailData({
      to: buyerEmail,
      subject: `Dispatch Note - ${dispatch.dispatchNumber} for Order ${dispatch.salesOrder?.orderNumber || 'N/A'}`,
      message: `Dear ${dispatch.salesOrder?.buyer?.business_name || 'Customer'},

Please find attached the dispatch note and invoice for your order.

Dispatch Details:
- Dispatch Number: ${dispatch.dispatchNumber}
- Order Number: ${dispatch.salesOrder?.orderNumber || 'N/A'}
- Dispatch Date: ${new Date(dispatch.dispatchDate).toLocaleDateString()}
- Vehicle: ${dispatch.vehicleNumber || 'N/A'}
- Driver: ${dispatch.driverName || 'N/A'}

Items Dispatched:
${dispatch.items?.map(item =>
        `- ${item.salesOrderItem?.product?.productName || 'N/A'}: ${item.quantityDispatched} ${item.unit}`
      ).join('\n') || 'No items listed'}

Please acknowledge receipt of this dispatch.

Best regards,
${companySettings?.companyName || 'Your Company Name'}`
    });

    setInvoiceFile(null);
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailData.to || !emailData.subject) {
      toast.error("Please fill in recipient email and subject");
      return;
    }

    setSendingEmail(true);
    try {
      await axios.post(
        `${config.API_BASE_URL}/dispatches/${selectedDispatch._id}/send-email`,
        {
          to: emailData.to,
          subject: emailData.subject,
          message: emailData.message
        }
      );

      toast.success("Email sent successfully!");
      setShowEmailModal(false);
      // Refresh the dispatches to show the email sent status
      fetchDispatches();
    } catch (err) {
      toast.error("Error sending email");
      console.error(err);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleOpenInvoiceModal = (dispatch) => {
    setSelectedDispatch(dispatch);
    setInvoiceUploadFile(null);
    setShowInvoiceModal(true);
  };

  const handleUploadInvoice = async () => {
    if (!invoiceUploadFile) {
      toast.error("Please select an invoice file to upload");
      return;
    }

    setUploadingInvoice(true);
    try {
      const formData = new FormData();
      formData.append('invoice', invoiceUploadFile);

      await axios.patch(
        `${config.API_BASE_URL}/dispatches/${selectedDispatch._id}/invoice`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success("Invoice uploaded successfully!");
      setShowInvoiceModal(false);
      // Refresh the dispatches to show the invoice download button
      fetchDispatches();
    } catch (err) {
      toast.error("Error uploading invoice");
      console.error(err);
    } finally {
      setUploadingInvoice(false);
    }
  };

  const handleDeleteInvoice = async (dispatch) => {
    if (!window.confirm(`Are you sure you want to delete the invoice for dispatch ${dispatch.dispatchNumber}?`)) {
      return;
    }

    try {
      // Send null or empty value to remove the invoice
      const formData = new FormData();
      formData.append('invoice', ''); // Empty string to indicate deletion

      await axios.patch(
        `${config.API_BASE_URL}/dispatches/${dispatch._id}/invoice`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success("Invoice deleted successfully!");
      // Refresh the dispatches to hide the invoice download button
      fetchDispatches();
    } catch (err) {
      toast.error("Error deleting invoice");
      console.error(err);
    }
  };

  const handleOpenViewEditModal = (dispatch) => {
    setSelectedDispatch(dispatch);
    setShowViewEditModal(true);
  };

  const handleOpenEditModal = (dispatch) => {
    setSelectedDispatch(dispatch);
    setEditData({
      vehicleNumber: dispatch.vehicleNumber || "",
      driverName: dispatch.driverName || "",
      driverContact: dispatch.driverContact || "",
      transporter: dispatch.transporter || "",
      dispatchDate: dispatch.dispatchDate ? new Date(dispatch.dispatchDate).toISOString().split('T')[0] : "",
      expectedDeliveryDate: dispatch.expectedDeliveryDate ? new Date(dispatch.expectedDeliveryDate).toISOString().split('T')[0] : "",
      remarks: dispatch.remarks || ""
    });
    setShowEditModal(true);
  };

  const handleUpdateDispatch = async () => {
    setUpdatingDispatch(true);
    try {
      await axios.patch(
        `${config.API_BASE_URL}/dispatches/${selectedDispatch._id}`,
        editData
      );

      toast.success("Dispatch updated successfully!");
      setShowEditModal(false);
      fetchDispatches();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error("Error updating dispatch");
      console.error(err);
    } finally {
      setUpdatingDispatch(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      Preparing: "secondary",
      Ready: "info",
      "In Transit": "warning",
      Delivered: "success",
      Cancelled: "danger"
    };
    return <Badge bg={map[status] || "secondary"}>{status}</Badge>;
  };

  const getDeliveryStatusBadge = (status) => {
    const map = {
      Pending: "secondary",
      "In Transit": "warning",
      Delivered: "success",
      Failed: "danger"
    };
    return <Badge bg={map[status] || "secondary"}>{status}</Badge>;
  };

  if (loading) {
    return <div className="text-center py-3">Loading dispatches...</div>;
  }

  return (
    <>
      <div className="mb-3">
        <h6>Dispatch History</h6>
        {dispatches.length === 0 ? (
          <div className="alert alert-info">No dispatch notes created yet.</div>
        ) : (
          <Table bordered hover responsive size="sm">
            <thead className="table-light">
              <tr>
                <th>Dispatch No</th>
                <th>Dispatch Date</th>
                <th>Created At</th>
                <th>Type</th>
                {/* <th>Vehicle</th> */}
                {/* <th>Driver</th> */}
                <th>Items</th>
                <th>Vehicle Weight</th>
                <th>Status</th>
                <th>Delivery Status</th>
                <th>Invoice</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dispatches.map((dispatch) => (
                <tr key={dispatch._id}>
                  <td>
                    <strong>{dispatch.dispatchNumber}</strong>
                  </td>
                  <td>{new Date(dispatch.dispatchDate).toLocaleDateString()}</td>
                  <td>{new Date(dispatch.createdAt).toLocaleDateString()}</td>
                  <td>{dispatch.dispatchType}</td>
                  {/* <td>{dispatch.vehicleNumber || "-"}</td> */}
                  {/* <td>{dispatch.driverName || "-"}</td> */}
                  <td>
                    <div className="d-flex flex-column gap-1" style={{ maxWidth: '200px' }}>
                      {dispatch.items && dispatch.items.length > 0 ? (
                        dispatch.items.map((item, index) => (
                          <div key={index} className="small">
                            <strong>{item.salesOrderItem?.product?.productName || "N/A"}</strong>
                            <br />
                            <span className="text-muted">
                              Qty: {item.quantityDispatched} {item.unit}
                              {/* {item.quality && ` (${item.quality})`} */}
                              {item.salesOrderItem && ` (${item.salesOrderItem.packingBagType.alias})`}
                            </span>
                          </div>
                        ))
                      ) : (
                        <span className="text-muted">No items</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-1">
                      <div>
                        <small className="text-muted">Before:</small>{" "}
                        {dispatch.vehicleWeightBeforeLoad ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            onClick={() => handleViewWeightSlip(dispatch.weightSlipBefore)}
                            title="View before load weight slip"
                          >
                            {dispatch.vehicleWeightBeforeLoad} kg
                          </Button>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </div>
                      <div>
                        <small className="text-muted">After:</small>{" "}
                        {dispatch.vehicleWeightAfterLoad ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            onClick={() => handleViewWeightSlip(dispatch.weightSlipAfter)}
                            title="View after load weight slip"
                          >
                            {dispatch.vehicleWeightAfterLoad} kg
                          </Button>
                        ) : (
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handleOpenWeightModal(dispatch)}
                            title="Add after load weight"
                          >
                            <FaWeightHanging /> Add
                          </Button>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-1">
                      {getStatusBadge(dispatch.status)}
                      {dispatch.emailSent && (
                        <Badge bg="success" className="mt-1">
                          <FaEnvelope className="me-1" />
                          Email Sent
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td>{getDeliveryStatusBadge(dispatch.deliveryStatus)}</td>
                  <td>
                    {dispatch.invoiceFile ? (
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm" id={`invoice-dropdown-${dispatch._id}`}>
                          <FaFileInvoice className="me-1" />
                          Invoice
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => handleDownloadInvoice(dispatch._id, dispatch.dispatchNumber)}
                            disabled={downloadingInvoice === dispatch._id}
                          >
                            {downloadingInvoice === dispatch._id ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Downloading...
                              </>
                            ) : (
                              <>
                                <FaDownload className="me-2" />
                                Download Invoice
                              </>
                            )}
                          </Dropdown.Item>
                          {canEditDispatch && (
                            <>
                              {!dispatch.emailSent && (
                                <>
                                  <Dropdown.Item onClick={() => handleOpenInvoiceModal(dispatch)}>
                                    <FaEdit className="me-2" />
                                    Change Invoice
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => handleDeleteInvoice(dispatch)}
                                    className="text-danger"
                                  >
                                    <FaTrash className="me-2" />
                                    Delete Invoice
                                  </Dropdown.Item>
                                </>
                              )}
                            </>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    ) : (
                      canEditDispatch && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleOpenInvoiceModal(dispatch)}
                          title="Upload Invoice"
                        >
                          <FaFileInvoice className="me-1" />
                          Upload
                        </Button>
                      )
                    )}
                  </td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary" size="sm" id={`actions-dropdown-${dispatch._id}`}>
                        Actions
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleOpenViewEditModal(dispatch)}>
                          <FaWrench className="me-2" />
                          View/Edit
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleViewDetails(dispatch._id)}>
                          <FaEye className="me-2" />
                          View Details
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleDownloadPDF(dispatch._id, dispatch.dispatchNumber)}
                          disabled={downloadingPdf === dispatch._id}
                        >
                          {downloadingPdf === dispatch._id ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Downloading PDF...
                            </>
                          ) : (
                            <>
                              <FaDownload className="me-2" />
                              Download PDF
                            </>
                          )}
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleOpenEmailModal(dispatch)}
                        // disabled={dispatch.emailSent}
                        >
                          <FaEnvelope className="me-2" />
                          Send Email
                          {/* {dispatch.emailSent ? 'Email Sent' : 'Send Email'} */}
                        </Dropdown.Item>
                        {canEditDispatch && (
                          <>
                            <Dropdown.Divider />
                            {!dispatch.emailSent && (
                              <Dropdown.Item onClick={() => handleOpenEditModal(dispatch)}>
                                <FaEdit className="me-2" />
                                Edit Dispatch
                              </Dropdown.Item>
                            )}
                            <Dropdown.Item onClick={() => handleOpenStatusModal(dispatch)}>
                              <FaTruck className="me-2" />
                              Update Status
                            </Dropdown.Item>
                            {!dispatch.vehicleWeightAfterLoad && (
                              <Dropdown.Item onClick={() => handleOpenWeightModal(dispatch)}>
                                <FaWeightHanging className="me-2" />
                                Add Weight
                              </Dropdown.Item>
                            )}
                          </>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      {/* Dispatch Details Modal */}
      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Dispatch Note Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDispatch && (
            <>
              <Row>
                <Col md={6}>
                  <p><strong>Dispatch Number:</strong> {selectedDispatch.dispatchNumber}</p>
                  <p><strong>Dispatch Date:</strong> {new Date(selectedDispatch.dispatchDate).toLocaleDateString()}</p>
                  <p><strong>Created At:</strong> {new Date(selectedDispatch.createdAt).toLocaleString()}</p>
                  <p><strong>Type:</strong> {selectedDispatch.dispatchType}</p>
                  <p><strong>Status:</strong> {getStatusBadge(selectedDispatch.status)}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Vehicle:</strong> {selectedDispatch.vehicleNumber || "-"}</p>
                  <p><strong>Driver:</strong> {selectedDispatch.driverName || "-"}</p>
                  <p><strong>Driver Contact:</strong> {selectedDispatch.driverContact || "-"}</p>
                  <p><strong>Transporter:</strong> {selectedDispatch.transporter || "-"}</p>
                </Col>
              </Row>

              {/* Vehicle Weight Information */}
              <Row className="mt-3">
                <Col md={12}>
                  <h6>Vehicle Weight Information</h6>
                  <Row>
                    <Col md={6}>
                      <p>
                        <strong>Tare Weight:</strong>{" "}
                        {selectedDispatch.vehicleWeightBeforeLoad ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            onClick={() => handleViewWeightSlip(selectedDispatch.weightSlipBefore)}
                            title="View tare weight slip"
                          >
                            {selectedDispatch.vehicleWeightBeforeLoad} kg
                          </Button>
                        ) : (
                          <span className="text-muted">Not recorded</span>
                        )}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p>
                        <strong>Gross Weight:</strong>{" "}
                        {selectedDispatch.vehicleWeightAfterLoad ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            onClick={() => handleViewWeightSlip(selectedDispatch.weightSlipAfter)}
                            title="View gross weight slip"
                          >
                            {selectedDispatch.vehicleWeightAfterLoad} kg
                          </Button>
                        ) : (
                          <span className="text-muted">Not recorded</span>
                        )}
                      </p>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <hr />

              <h6>Dispatched Items</h6>
              <Table bordered size="sm">
                <thead className="table-light">
                  <tr>
                    <th>Product</th>
                    <th>Quality</th>
                    <th>Quantity</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedDispatch.items?.map((item, index) => (
                    <tr key={index}>
                      <td>{item.salesOrderItem?.product?.productName || "N/A"}</td>
                      <td>{item.stockAllocation?.quality || "-"}</td>
                      <td>{item.quantityDispatched} {item.unit}</td>
                      <td>{item.warehouseLocationId?.label || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {selectedDispatch.remarks && (
                <>
                  <h6>Remarks</h6>
                  <p className="text-muted">{selectedDispatch.remarks}</p>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Status Update Modal */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Update Dispatch Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>New Status</Form.Label>
            <Form.Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
            >
              <option value="In Transit">In Transit</option>
              <option value="Delivered">Delivered</option>
              <option value="Cancelled">Cancelled</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateStatus}>
            Update Status
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Gross Weight Update Modal */}
      <Modal show={showWeightModal} onHide={() => setShowWeightModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add After Load Weight</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDispatch && (
            <Alert variant="info">
              <strong>Dispatch:</strong> {selectedDispatch.dispatchNumber}<br />
              <strong>Vehicle:</strong> {selectedDispatch.vehicleNumber || "N/A"}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Gross Vehicle Weight (Loaded Truck) <small className="text-muted">(kg)</small></Form.Label>
            <Form.Control
              type="number"
              value={vehicleWeightAfterLoad}
              onChange={(e) => {
                setVehicleWeightAfterLoad(e.target.value);
                setWeightValidationError("");
              }}
              placeholder="e.g., 8500"
              min="0"
              step="0.01"
              required
              isInvalid={!!weightValidationError}
            />
            <Form.Control.Feedback type="invalid">
              {weightValidationError}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Gross Weight Slip (Loaded Truck Kata Parchi)</Form.Label>
            <Form.Control
              type="file"
              onChange={(e) => setWeightSlipAfter(e.target.files[0])}
              accept=".pdf,.jpg,.jpeg,.png"
            />
            <Form.Text className="text-muted">
              Upload weight slip document (PDF, JPG, PNG)
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowWeightModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateWeight}
            disabled={!vehicleWeightAfterLoad}
          >
            Update Weight
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Email Modal */}
      <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Send Dispatch Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDispatch && (
            <Alert variant="info">
              <strong>Dispatch:</strong> {selectedDispatch.dispatchNumber}<br />
              <strong>Order:</strong> {selectedDispatch.salesOrder?.orderNumber || 'N/A'}<br />
              <strong>Customer:</strong> {selectedDispatch.salesOrder?.buyer?.business_name || 'N/A'}
            </Alert>
          )}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>To <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                value={emailData.to}
                onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                placeholder="recipient@example.com"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Subject <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                placeholder="Email subject"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={emailData.message}
                onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                placeholder="Email message"
              />
            </Form.Group>

            <Alert variant="info">
              <strong>Note:</strong> The dispatch note PDF will be automatically attached to this email.
              {selectedDispatch?.invoiceFile && " The invoice will also be attached if available."}
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEmailModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSendEmail}
            disabled={sendingEmail || !emailData.to || !emailData.subject}
          >
            {sendingEmail ? "Sending..." : "Send Email"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Invoice Upload Modal */}
      <Modal show={showInvoiceModal} onHide={() => setShowInvoiceModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDispatch && (
            <Alert variant="info">
              <strong>Dispatch:</strong> {selectedDispatch.dispatchNumber}<br />
              <strong>Order:</strong> {selectedDispatch.salesOrder?.orderNumber || 'N/A'}<br />
              <strong>Customer:</strong> {selectedDispatch.salesOrder?.buyer?.business_name || 'N/A'}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Invoice File <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="file"
              onChange={(e) => setInvoiceUploadFile(e.target.files[0])}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              required
            />
            <Form.Text className="text-muted">
              Upload invoice document (PDF, DOC, DOCX, JPG, PNG)
            </Form.Text>
            {invoiceUploadFile && (
              <div className="mt-2">
                <small className="text-success">Selected: {invoiceUploadFile.name}</small>
              </div>
            )}
          </Form.Group>

          <Alert variant="info">
            <strong>Note:</strong> The uploaded invoice will be automatically attached to dispatch emails sent for this dispatch.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInvoiceModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUploadInvoice}
            disabled={uploadingInvoice || !invoiceUploadFile}
          >
            {uploadingInvoice ? "Uploading..." : "Upload Invoice"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ViewEditDispatchModal
        show={showViewEditModal}
        onHide={() => setShowViewEditModal(false)}
        dispatchId={selectedDispatch?._id}
        onRefresh={() => {
          fetchDispatches();
          if (onRefresh) onRefresh();
        }}
      />

      {/* Edit Dispatch Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Dispatch Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* {selectedDispatch && (
            <Alert variant="info">
              <strong>Dispatch:</strong> {selectedDispatch.dispatchNumber}<br />
              <strong>Order:</strong> {selectedDispatch.salesOrder?.orderNumber || 'N/A'}<br />
              <strong>Customer:</strong> {selectedDispatch.salesOrder?.buyer?.business_name || 'N/A'}
            </Alert>
          )} */}

          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={editData.vehicleNumber}
                    onChange={(e) => setEditData({ ...editData, vehicleNumber: e.target.value })}
                    placeholder="e.g., MH-12-AB-1234"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Driver Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editData.driverName}
                    onChange={(e) => setEditData({ ...editData, driverName: e.target.value })}
                    placeholder="Driver name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Driver Contact</Form.Label>
                  <Form.Control
                    type="text"
                    value={editData.driverContact}
                    onChange={(e) => setEditData({ ...editData, driverContact: e.target.value })}
                    placeholder="Contact number"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Transporter</Form.Label>
                  <Form.Control
                    type="text"
                    value={editData.transporter}
                    onChange={(e) => setEditData({ ...editData, transporter: e.target.value })}
                    placeholder="Transporter name"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Dispatch Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={editData.dispatchDate}
                    onChange={(e) => setEditData({ ...editData, dispatchDate: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Expected Delivery Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={editData.expectedDeliveryDate}
                    onChange={(e) => setEditData({ ...editData, expectedDeliveryDate: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editData.remarks}
                onChange={(e) => setEditData({ ...editData, remarks: e.target.value })}
                placeholder="Additional remarks"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateDispatch}
            disabled={updatingDispatch}
                    >
                      {updatingDispatch ? "Updating..." : "Update Dispatch"}
                    </Button>
                  </Modal.Footer>
                </Modal>
              </>
            );
          }
          
